import React from 'react';
import { Package, AlertTriangle } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface StockLevelChartProps {
  stockItems: StockItem[];
}

const StockLevelChart: React.FC<StockLevelChartProps> = ({ stockItems }) => {
  // カテゴリ別在庫状況集計
  const categoryStats = stockItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        total: 0,
        optimal: 0,
        low: 0,
        critical: 0,
        excess: 0
      };
    }
    acc[item.category].total++;
    acc[item.category][item.status]++;
    return acc;
  }, {} as Record<string, any>);

  // 在庫レベル可視化データ
  const topItems = stockItems.slice(0, 8);

  return (
    <div className="space-y-4">
      {/* カテゴリ別サマリー */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(categoryStats).map(([category, stats]) => (
          <Card key={category} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm" style={{ color: colors.text.primary }}>
                {category}
              </h4>
              <Package size={16} style={{ color: colors.text.secondary }} />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span style={{ color: colors.text.secondary }}>適正在庫</span>
                <span className="font-medium" style={{ color: colors.support.success }}>
                  {stats.optimal}品
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: colors.text.secondary }}>低在庫</span>
                <span className="font-medium" style={{ color: colors.status.warning }}>
                  {stats.low}品
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: colors.text.secondary }}>危険在庫</span>
                <span className="font-medium" style={{ color: colors.status.error }}>
                  {stats.critical}品
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: colors.text.secondary }}>過剰在庫</span>
                <span className="font-medium" style={{ color: colors.text.secondary }}>
                  {stats.excess}品
                </span>
              </div>
            </div>
            
            {/* プログレスバー */}
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden flex">
              <div 
                className="h-full transition-all"
                style={{ 
                  width: `${(stats.optimal / stats.total) * 100}%`,
                  backgroundColor: colors.support.success 
                }}
              />
              <div 
                className="h-full transition-all"
                style={{ 
                  width: `${(stats.low / stats.total) * 100}%`,
                  backgroundColor: colors.status.warning 
                }}
              />
              <div 
                className="h-full transition-all"
                style={{ 
                  width: `${(stats.critical / stats.total) * 100}%`,
                  backgroundColor: colors.status.error 
                }}
              />
              <div 
                className="h-full transition-all"
                style={{ 
                  width: `${(stats.excess / stats.total) * 100}%`,
                  backgroundColor: colors.text.secondary 
                }}
              />
            </div>
          </Card>
        ))}
      </div>

      {/* 在庫レベルビジュアライゼーション */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          在庫レベル状況
        </h3>
        
        <div className="space-y-3">
          {topItems.map(item => {
            const stockPercentage = (item.currentStock / item.maxStock) * 100;
            const optimalPercentage = (item.optimalStock / item.maxStock) * 100;
            const minPercentage = (item.minStock / item.maxStock) * 100;
            
            return (
              <div key={item.skuId} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium" style={{ color: colors.text.primary }}>
                      {item.skuName}
                    </span>
                    {item.status === 'critical' && (
                      <AlertTriangle size={14} style={{ color: colors.status.error }} />
                    )}
                  </div>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {item.currentStock} / {item.optimalStock} ({item.stockDays}日分)
                  </span>
                </div>
                
                <div className="relative h-6 bg-gray-100 rounded">
                  {/* 最小在庫ライン */}
                  <div 
                    className="absolute top-0 h-full w-0.5 bg-red-500"
                    style={{ left: `${minPercentage}%` }}
                    title="最小在庫"
                  />
                  
                  {/* 適正在庫ライン */}
                  <div 
                    className="absolute top-0 h-full w-0.5 bg-green-500"
                    style={{ left: `${optimalPercentage}%` }}
                    title="適正在庫"
                  />
                  
                  {/* 現在在庫バー */}
                  <div 
                    className="absolute top-0 h-full rounded transition-all"
                    style={{ 
                      width: `${Math.min(100, stockPercentage)}%`,
                      backgroundColor: item.status === 'critical' ? colors.status.error :
                                     item.status === 'low' ? colors.status.warning :
                                     item.status === 'excess' ? colors.accent.purple :
                                     colors.support.success
                    }}
                  />
                  
                  {/* ラベル */}
                  <div className="absolute inset-0 flex items-center px-2">
                    <span className="text-xs font-medium text-white">
                      {stockPercentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* 凡例 */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: colors.ui.border }}>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.support.success }} />
            <span style={{ color: colors.text.secondary }}>適正</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.status.warning }} />
            <span style={{ color: colors.text.secondary }}>低在庫</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.status.error }} />
            <span style={{ color: colors.text.secondary }}>危険</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.accent.purple }} />
            <span style={{ color: colors.text.secondary }}>過剰</span>
          </div>
          <div className="flex items-center gap-2 text-xs ml-auto">
            <div className="w-0.5 h-3" style={{ backgroundColor: colors.support.success }} />
            <span style={{ color: colors.text.secondary }}>適正ライン</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-0.5 h-3" style={{ backgroundColor: colors.status.error }} />
            <span style={{ color: colors.text.secondary }}>最小ライン</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StockLevelChart;
